import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';

class BbdAtcUpsellsComponent extends DialogComponent {
  /** @type {HTMLButtonElement | null} */
  #showCartBtn = null;
  /** @type {HTMLButtonElement | null} */
  #closeBtn = null;
  /** @type {HTMLElement | null} */
  #chosenContainer = null;
  /** @type {Map<string, HTMLElement>} */
  #upsellItemByProductId = new Map();
  /** @type {((e: Event) => void) | null} */
  #boundCloseHandler = null;

  connectedCallback() {
    super.connectedCallback();

    this.#cacheRefs();
    this.#indexUpsellItems();

    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);

    // Wire footer buttons
    this.#showCartBtn?.addEventListener('click', this.#handleShowCart);

    // Delegate quick-remove clicks
    this.addEventListener('click', this.#onClick);

    // Close button behavior: prefer declarative on:click="/closeDialog" if present,
    // otherwise attach a JS fallback listener for themes/environments without the action system.
    const hasDeclarativeClose = this.#closeBtn?.hasAttribute('on:click');
    if (!hasDeclarativeClose && this.#closeBtn) {
      this.#boundCloseHandler = () => this.closeDialog();
      this.#closeBtn.addEventListener('click', this.#boundCloseHandler);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    this.#showCartBtn?.removeEventListener('click', this.#handleShowCart);
    this.removeEventListener('click', this.#onClick);
    if (this.#boundCloseHandler && this.#closeBtn) {
      this.#closeBtn.removeEventListener('click', this.#boundCloseHandler);
      this.#boundCloseHandler = null;
    }
  }

  #cacheRefs() {
    this.#showCartBtn = this.querySelector('[data-action="show-cart"]');
    this.#closeBtn = this.querySelector('.bbd-atc-upsells__close');
    this.#chosenContainer = this.querySelector('.bbd-atc-upsells__chosen');
  }

  #indexUpsellItems() {
    this.#upsellItemByProductId.clear();
    // After moving Quick Add inside bbd-product-row, each upsell item is the row itself
    const rows = this.querySelectorAll('.bbd-product-row[data-product-id]');
    for (const row of rows) {
      const id = row.getAttribute('data-product-id');
      if (id) this.#upsellItemByProductId.set(id, row);
    }
  }

  /** @param {Event & { detail?: any }} evt */
  #onCartAdd = (evt) => {
    // Only react to true add-to-cart events originating from the product form
    const source = evt?.detail?.data?.source;
    const didError = evt?.detail?.data?.didError === true;
    const productId = evt?.detail?.data?.productId?.toString?.();

    // Cart removes/updates also emit ThemeEvents.cartUpdate; ignore those
    if (source !== 'product-form-component' || didError) return;

    // If the event originated inside this upsells element (i.e., quick-add inside drawer),
    // highlight that product row and append to the chosen list.
    if (evt.target && this.contains(evt.target)) {
      if (!productId) return;
      const variantId = evt?.detail?.sourceId?.toString?.();
      this.#markUpsellChosen(productId, variantId);
      return;
    }

    // Otherwise, it likely came from the main product form. Only open when upsells exist and
    // the product matches our main product context.
    const hasUpsells = this.dataset.hasUpsells === 'true';
    if (!hasUpsells) return;

    const mainProductId = this.dataset.mainProductId;
    if (productId && mainProductId && productId.toString() === mainProductId.toString()) {
      // Verified add-to-cart of the main product; open the upsells drawer.
      this.open();
    }
  };

  /** @param {MouseEvent} evt */
  #onClick = (evt) => {
    const btn = /** @type {HTMLElement|null} */ (
      evt.target instanceof HTMLElement ? evt.target.closest('[data-action="quick-remove"]') : null
    );
    if (!btn) return;

    const row = btn.closest('.bbd-product-row');
    if (!row) return;

    const variantId = row.getAttribute('data-variant-id');
    if (!variantId) return;

    this.#decrementVariant(variantId, row);
  };

  /**
   * Decrement quantity of a variant in the cart by 1 using /cart/update.js
   * @param {string} variantId
   * @param {Element} rowEl - the product row element where action originated
   */
  async #decrementVariant(variantId, rowEl) {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const line = cart.items.find((it) => String(it.variant_id) === String(variantId));
      if (!line) return;

      const newQty = Math.max(0, Number(line.quantity) - 1);
      const formData = new FormData();
      formData.append(`updates[${variantId}]`, String(newQty));

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const updated = await res.json();

      // Dispatch a cart update event so other components can react
      document.dispatchEvent(
        new CartUpdateEvent(updated, 'bbd-atc-upsells-quick-remove', { source: 'bbd-atc-upsells-quick-remove' })
      );

      // UI updates within upsells
      // Regardless of the remaining cart quantity, pressing quick-remove in the upsells drawer
      // should clear the visual selection: deactivate the row and remove it from the chosen list.
      const productId = rowEl.getAttribute('data-product-id');
      if (!productId) return;

      const originalRow = this.#upsellItemByProductId.get(productId);
      originalRow?.classList.remove('active');
      const chosen = this.#chosenContainer?.querySelector(`.bbd-product-row[data-product-id="${productId}"]`);
      chosen?.parentElement?.removeChild(chosen);
    } catch (e) {
      console.error('Quick remove failed', e);
    }
  }

  /**
   * Highlights the upsell product row and appends/updates a clone under the main product chosen list.
   * Ensures the correct variant id (from the ATC event) is stored on both the source row and the clone
   * so that quick-remove in the chosen list decrements the expected line item.
   * @param {string} productId
   * @param {string | undefined} variantId
   */
  #markUpsellChosen(productId, variantId) {
    const row = this.#upsellItemByProductId.get(productId);
    if (!row) return;

    // Update the row's variant id to the actually added variant, if provided
    if (variantId) {
      row.setAttribute('data-variant-id', variantId);
    }

    // Add active class to the product row in the accordion
    row.classList.add('active');

    if (!this.#chosenContainer) return;
    const existing = this.#chosenContainer.querySelector(`.bbd-product-row[data-product-id="${productId}"]`);
    if (existing) {
      // Update existing chosen entry variant id
      if (variantId) existing.setAttribute('data-variant-id', variantId);
      return;
    }

    const clone = row.cloneNode(true);
    if (clone) {
      if (variantId) clone.setAttribute('data-variant-id', variantId);
      this.#chosenContainer.appendChild(clone);
    }
  }

  #handleShowCart = () => {
    this.closeDialog();
    const cartDrawer = document.querySelector('cart-drawer-component');
    cartDrawer?.open?.();
  };

  /**
   * Reset component state and open the dialog.
   * Ensures no previously chosen items or active states persist between opens.
   */
  open() {
    this.#resetState();
    this.showDialog();
  }

  /**
   * Clears chosen list and removes active states from upsell rows.
   */
  #resetState() {
    // Clear chosen items list
    if (this.#chosenContainer) {
      this.#chosenContainer.innerHTML = '';
    }

    // Remove active class from all indexed upsell rows
    if (this.#upsellItemByProductId.size === 0) {
      // in case indexing hasn't occurred yet or DOM changed, re-index
      this.#indexUpsellItems();
    }
    for (const row of this.#upsellItemByProductId.values()) {
      row.classList.remove('active');
    }
  }
}

if (!customElements.get('bbd-atc-upsells')) {
  customElements.define('bbd-atc-upsells', BbdAtcUpsellsComponent);
}
